import { makeStyles, Typography } from "@material-ui/core";
import { strings } from "@resource/common";
import SelectInterviews from "components/BulkEdit/EditInterviewKits/SelectInterviews";
import Dialog from "components/legacy/Dialog/Dialog";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import _ from "lodash";
import React, { Reducer, useReducer } from "react";
import { ResourceCustomTheme } from "styles/config";

import ProgressStepper from "../ProgressStepper";
import ConfirmInterviewEdits from "./ConfirmInterviewEdits";
import EditInterviews from "./EditInterviews";
import {
  ActionEnum,
  EditActionEnum,
  InterviewEditsDispatchType,
  InterviewEditsStateType,
} from "./types";

enum ProgressStepEnum {
  Cancel = -1,
  SetInterviews = 0,
  SetEdits = 1,
  ConfirmEdits = 2,
  CloseModal = 3,
}

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  contextLabel: {
    marginBottom: theme.spacing(2),
  },
  emphasize: {
    color: theme.colors.RoyalPurple(),
  },
  titleContainer: {
    textAlign: "center",
  },
}));

type BulkEditInterviewKitsModalProps = {
  jobRoleIds: string[];
  open: boolean;
  onCancel: () => void;
  onClose: () => void;
  onExited: () => void;
};

const BulkEditInterviewKitsModal: React.FC<BulkEditInterviewKitsModalProps> = ({
  jobRoleIds,
  open,
  onCancel,
  onExited,
  onClose,
}) => {
  const classes = useStyles();

  const reducer = (
    state: InterviewEditsStateType,
    action: InterviewEditsDispatchType
  ): InterviewEditsStateType => {
    switch (action.type) {
      case ActionEnum.SetInterviews:
        return {
          ...state,
          interviewIds: action.interviewSelection?.ids ?? [],
          numStages: action.interviewSelection?.numStages ?? 0,
        };
      case ActionEnum.SetEditAction:
        return {
          ...state,
          editAction: action.editAction ?? state.editAction,
        };
      case ActionEnum.SetEdits:
        return {
          ...state,
          ..._.omit(action, "type"),
        };
      case ActionEnum.Next:
        return {
          ...state,
          activeStepIndex:
            state.editAction === EditActionEnum.Show
              ? state.activeStepIndex + 1
              : ProgressStepEnum.ConfirmEdits,
        };
      case ActionEnum.Back:
        return {
          ...state,
          activeStepIndex:
            state.editAction === EditActionEnum.Show
              ? state.activeStepIndex - 1
              : ProgressStepEnum.SetInterviews,
        };
      case ActionEnum.Cancel:
        onCancel();
        return state;
      case ActionEnum.Finish:
        onClose();
        return state;
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer<
    Reducer<InterviewEditsStateType, InterviewEditsDispatchType>
  >(reducer, {
    interviewIds: [],
    editAction: EditActionEnum.Show,
    interviewName: "",
    numStages: 0,
    titleOverride: "",
    descriptionOverride: "",
    activeStepIndex: 0,
  });

  const actionIsHide = state.editAction === EditActionEnum.Hide;

  let selectionCopy = "";
  if (state.activeStepIndex === ProgressStepEnum.SetInterviews) {
    selectionCopy = `You are ${
      actionIsHide ? "hiding" : "applying the following edits to"
    }`;
  } else {
    selectionCopy = `You are ${actionIsHide ? "hiding" : "editing"}`;
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      onExited={onExited}
      maxWidth="md"
      scroll="paper"
      title="Hide Interviews"
      fullWidth
      className={classes.root}
    >
      <DialogTitle onClose={onCancel}>
        <div className={classes.titleContainer}>
          <ProgressStepper
            steps={["Select Interviews", "Edit Interviews", "Confirm Edits"]}
            activeStep={state.activeStepIndex}
          />
          <Typography className={classes.contextLabel} variant="h4">
            {state.activeStepIndex === ProgressStepEnum.SetInterviews ? (
              <>
                You selected{" "}
                <span className={classes.emphasize}>
                  {strings.pluralize("job", jobRoleIds.length)}
                </span>
              </>
            ) : (
              <>
                {selectionCopy}{" "}
                <span className={classes.emphasize}>
                  {strings.pluralize("interview", state.interviewIds.length)}
                </span>{" "}
                within{" "}
                <span className={classes.emphasize}>
                  {strings.pluralize("job", jobRoleIds.length)}
                </span>
              </>
            )}
          </Typography>
        </div>
      </DialogTitle>
      {(() => {
        let ToRender;
        switch (state.activeStepIndex) {
          case ProgressStepEnum.SetInterviews:
            ToRender = (
              <SelectInterviews dispatch={dispatch} jobRoleIds={jobRoleIds} />
            );
            break;
          case ProgressStepEnum.SetEdits:
            ToRender = <EditInterviews state={state} dispatch={dispatch} />;
            break;
          case ProgressStepEnum.ConfirmEdits:
            ToRender = (
              <ConfirmInterviewEdits state={state} dispatch={dispatch} />
            );
            break;

          default:
            break;
        }
        return <>{ToRender}</>;
      })()}
    </Dialog>
  );
};

export default BulkEditInterviewKitsModal;
